import { supportedBrowsers } from 'corso-web-lib';
import { useEffect } from 'react';
import { Outlet, ScrollRestoration, useLocation, useMatch } from 'react-router';
import { twMerge } from 'tailwind-merge';
import Button from '~/components/button/Button';
import Icon from '~/components/Icon';
import Link from '~/components/Link';
import { formatDate } from '~/utils/formatters';
import { useLoaderData } from './appShell';

function AppBar() {
  const { store, order, logoUrl, storeName } = useLoaderData();
  const inactivePage = useMatch('/:store/inactive');
  const isRegisterPage = useMatch({ path: '/:store/register/', end: false });
  const location = useLocation();
  const indexRoute = useMatch(`/${store}`);

  const { orderNo, createdOn, wasCreatedFromRegistration, idFromPlatform } =
    order ?? {};

  const showDetail =
    orderNo &&
    createdOn &&
    !wasCreatedFromRegistration &&
    !indexRoute &&
    !inactivePage &&
    !isRegisterPage;

  // https://github.com/remix-run/react-router/discussions/9788#discussioncomment-4604278
  const canNavigateBack = location.key !== 'default';
  const orderHubPath = store
    ? `/${store}/order/${idFromPlatform}`
    : `/reorder/${idFromPlatform}`;

  return (
    <nav className="grid w-full grid-cols-3 items-center md:gap-x-6">
      {canNavigateBack && (
        <Button
          variant="ghost"
          className="max-w-fit not-embedded:hidden"
          onClick={() => window.history.back()}
        >
          <Icon icon="ChevronLeft" /> Back
        </Button>
      )}

      <img
        aria-hidden
        src={logoUrl}
        alt={`${storeName} logo`}
        className={twMerge(
          'col-span-2 h-10 justify-self-center bg-contain md:col-span-1 md:col-start-2 embedded:hidden',
          !showDetail && 'col-span-full',
        )}
      />

      {showDetail && (
        <div className="col-start-3 flex flex-col items-end justify-center gap-x-2 body-sm">
          <Link to={orderHubPath}>
            <Icon icon="House" /> {orderNo}
          </Link>
          <span>{formatDate(createdOn)}</span>
        </div>
      )}
    </nav>
  );
}

function Header() {
  const { store, registrationIndex } = useLoaderData();
  const isBrowserSupported = supportedBrowsers.test(navigator.userAgent);
  const isRegisterIndex = useMatch({
    path: `/:store/${registrationIndex}`,
    end: true,
  });
  const indexRoute = useMatch(`/${store}`);

  const hideOnEmbed = (!!indexRoute || !!isRegisterIndex) && isBrowserSupported;

  return (
    <header
      data-testid="app-header"
      data-component="corso-app-header"
      className={twMerge(
        'flex min-h-16 w-full flex-col items-center justify-center gap-4 rounded-lg bg-surface-container-lowest p-4 shadow-sm',
        hideOnEmbed && 'embedded:hidden',
      )}
    >
      {!isBrowserSupported && (
        <div
          // maybe should have some aria-role like alert? maybe it should use an alert/banner component?
          data-testid="browser-support-banner"
          className="w-full bg-critical px-4 py-2 text-center text-on-critical"
        >
          <p>
            Your browser appears to be out of date, and some features may not
            work as expected. Please update to a newer version if you experience
            any difficulties.
          </p>
        </div>
      )}
      <AppBar />
    </header>
  );
}

function Footer() {
  const { footerText } = useLoaderData();

  return (
    <footer
      className="flex flex-col items-center justify-center md:flex-row md:justify-start"
      data-testid="app-footer"
      data-component="corso-app-footer"
    >
      {footerText && <p className="body-sm">{footerText}</p>}
    </footer>
  );
}

function Content() {
  return (
    <div
      className="space-y-8 rounded-lg bg-surface-container px-4 py-8 shadow-sm"
      data-testid="app-content"
      data-component="corso-app-content"
    >
      <main>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

/**
 * Hook to load the Localize script
 * @param key string - the Localize key from the merchant
 */
function useLocalize(key?: string) {
  useEffect(() => {
    if (!key) {
      return () => {};
    }

    const script = document.createElement('script');
    script.src = 'https://global.localizecdn.com/localize.js';
    script.onload = () => {
      // @ts-expect-error - Localize script copied from the Localize docs
      // oxlint-disable-next-line @typescript-eslint/no-unused-expressions, func-names
      !(function (a) {
        // @ts-expect-error - Localize script copied from the Localize docs
        if (!a.Localize) {
          // @ts-expect-error - Localize script copied from the Localize docs
          // oxlint-disable-next-line no-param-reassign
          a.Localize = {};
          for (
            // oxlint-disable-next-line vars-on-top, no-var
            var e = [
                'translate',
                'untranslate',
                'phrase',
                'initialize',
                'translatePage',
                'setLanguage',
                'getLanguage',
                'getSourceLanguage',
                'detectLanguage',
                'getAvailableLanguages',
                'untranslatePage',
                'bootstrap',
                'prefetch',
                'on',
                'off',
                'hideWidget',
                'showWidget',
              ],
              t = 0;
            t < e.length;
            // oxlint-disable-next-line no-plusplus
            t++
          )
            // @ts-expect-error - Localize script copied from the Localize docs
            // oxlint-disable-next-line no-param-reassign func-names typescript/no-unsafe-member-access
            a.Localize[e[t]] = function () {
              /* empty */
            };
        }
      })(window);

      // @ts-expect-error - Localize script copied from the Localize docs
      // oxlint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      Localize.initialize({ key, rememberLanguage: true });
    };
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [key]);
}

export default function AppShell() {
  const { localizeKey } = useLoaderData();

  // Load the Localize script
  useLocalize(localizeKey);

  // Remove the splash screen
  useEffect(() => {
    document.body.dataset.ready = 'ready';
  }, []);

  return (
    <div
      // fills the screen with background
      className="min-h-screen bg-surface bg-cover bg-fixed body-lg text-on-surface md:bg-banner-img"
      data-testid="app-shell"
      data-component="corso-app-shell"
    >
      <div
        data-testid="app-body"
        // ! centers and constrains the app and used to measure height for embedded mode, the id is important
        id="app-body"
        data-component="corso-app-body"
        className="relative mx-auto w-full max-w-3xl space-y-6 p-2"
      >
        <Header />
        <Content />
        <ScrollRestoration />
      </div>
    </div>
  );
}
